import queryString from 'query-string';

import config from '../../config';
import defaultLocations from '../../default-location-searches';
import { getLocaleFromPath } from '../../util/locale';
import { logError } from '../../util/log';
import { getStoryblokClient } from '../../util/storyblok';
import { PRODUCTS_DATASOURCE } from '../../util/storyblokDatasources';
import { decodeLatLng, decodeLatLngBounds, parseFloatNum } from '../../util/urlHelpers';
import { searchListings } from '../SearchPage/SearchPage.duck';
import reducerRegistry from '../../ducks/reducerRegistry';

// ================ Action types ================ //
const FETCH_PRODUCTS_REQUEST = 'app/LandingPage/FETCH_PRODUCTS_REQUEST';
const FETCH_PRODUCTS_SUCCESS = 'app/LandingPage/FETCH_PRODUCTS_SUCCESS';
const FETCH_PRODUCTS_ERROR = 'app/LandingPage/FETCH_PRODUCTS_ERROR';

// ================ Reducer ================ //

/** @type {import('../../../types/vansite').VanSite.State.LandingPage} */
const initialState = {
  verifiedListingIds: [],
  verifiedListingsInProgress: false,
  verifiedListingsInError: false,
  products: null,
  productsLocale: null,
  productsLoading: false,
  productsError: null,
};

const reducer = (state = initialState, action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case FETCH_PRODUCTS_REQUEST:
      return { ...state, productsLoading: true, productsError: null };

    case FETCH_PRODUCTS_SUCCESS:
      return {
        ...state,
        productsLoading: false,
        products: payload.products,
        productsLocale: payload.locale,
      };

    case FETCH_PRODUCTS_ERROR:
      return { ...state, productsLoading: false, productsError: payload };

    default:
      return state;
  }
};

export default reducer;

// ================ Action creators ================ //
export const fetchProductsRequest = () => ({
  type: FETCH_PRODUCTS_REQUEST,
});

export const fetchProductsSuccess = (payload) => ({
  type: FETCH_PRODUCTS_SUCCESS,
  payload,
});

export const fetchProductsError = (error) => ({
  type: FETCH_PRODUCTS_ERROR,
  error: true,
  payload: error,
});

// ================ Thunks ================ //
export const fetchProducts = (locale) => async (dispatch, getState) => {
  if (getState().LandingPage?.products && getState().LandingPage.productsLocale === locale) {
    return;
  }
  dispatch(fetchProductsRequest());

  try {
    const storyblokApi = getStoryblokClient();

    // Define the data sources to fetch
    const response = await storyblokApi.get('cdn/datasource_entries', {
      version: process.env.REACT_APP_ENV === 'production' ? 'published' : 'draft',
      datasource: PRODUCTS_DATASOURCE,
    });
    const datasources = response.data.datasource_entries[0].value.split(',');
    const fetchDatasourceEntries = async (datasource) => {
      const response = await storyblokApi.get('cdn/datasource_entries', {
        version: process.env.REACT_APP_ENV === 'production' ? 'published' : 'draft',
        datasource,
        dimension: ['de', 'en', 'fr', 'it'].includes(locale) ? locale : 'de',
      });
      return response.data.datasource_entries.reduce((acc, entry) => {
        acc[entry.name] = entry.dimension_value || entry.value;
        return acc;
      }, {});
    };

    // Fetch all product data concurrently
    const productData = await Promise.all(
      datasources.map((datasource) => fetchDatasourceEntries(datasource)),
    );

    // Combine into a single object
    const products = productData.reduce((acc, data, currentIndex) => {
      acc[`product${currentIndex + 1}`] = data;
      return acc;
    }, {});

    dispatch(fetchProductsSuccess({ products, locale }));
  } catch (error) {
    logError(error, 'fetch-products-failed');
    dispatch(fetchProductsError(error));
  }
};

/**
 * Parse a URL search query. Converts numeric values into numbers,
 * 'true' and 'false' as booleans, and serialised LatLng and
 * LatLngBounds into respective instances based on given options.
 *
 * @param {string} search - query string to parse, optionally with a
 * leading '?' or '#' character
 *
 * @param {object} options - Options for parsing:
 *
 * - latlng {Array<String} keys to parse as LatLng instances, null if
 *   not able to parse
 * - latlngBounds {Array<String} keys to parse as LatLngBounds
 *   instances, null if not able to parse
 *
 * @returns {object} key/value pairs parsed from the given String
 */
export const parse = (search, options = {}) => {
  const { latlng = [], latlngBounds = [] } = options;
  const params = queryString.parse(search);
  return Object.keys(params).reduce((result, key) => {
    const val = params[key];

    if (latlng.includes(key)) {
      result[key] = decodeLatLng(val);
    } else if (latlngBounds.includes(key)) {
      result[key] = decodeLatLngBounds(val);
    } else if (val === 'true') {
      result[key] = true;
    } else if (val === 'false') {
      result[key] = false;
    } else {
      const num = parseFloatNum(val);
      result[key] = num === null ? val : num;
    }

    return result;
  }, {});
};

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 16;
export const loadData = (params, search, match) => async (dispatch, getState) => {
  const locale = getLocaleFromPath(match.path);
  const defaultLocs = defaultLocations(locale);

  // Mapping of locale codes to specific default location IDs
  const localeToDefaultId = {
    de: 'default-germany',
    en: 'default-europe',
    fr: 'default-france',
    it: 'default-italy',
    es: 'default-spain',
    no: 'default-norway-and-sweden',
    sv: 'default-norway-and-sweden',
  };

  // Use the locale to find the specific default location ID, then find the location
  const defaultLocationId = localeToDefaultId[locale];
  const specificLocation = defaultLocs.find((loc) => loc.id === defaultLocationId);

  const {
    page = 1,
    origin,
    ...rest
  } = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  // Use the specific location's bounds if available
  const bounds = specificLocation ? specificLocation.predictionPlace.bounds : null;
  const originMaybe = config.sortSearchByDistance && origin ? { origin } : {};

  const apiQueryParams = {
    ...rest,
    ...originMaybe,
    page,
    perPage: RESULT_PAGE_SIZE,
    include: ['images'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'createdAt',
      'publicData.category',
      'publicData.locationPlace',
      'publicData.verified',
      'metadata.rating',
      'metadata.ratingCount',
      'metadata.explorerListing',
      'metadata.annualPassPrice',
    ],

    'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
    'limit.images': 1,
    pub_hidden: false,
    bounds: bounds,
  };

  try {
    await Promise.all([
      dispatch(searchListings(apiQueryParams, locale)),
      dispatch(fetchProducts(locale)), // Fetch products in parallel
    ]);
  } catch (error) {
    logError(error, 'load-data-failed');
  }
};

// Injected lazily: the reducer reaches the store when this (code-split) module is evaluated.
reducerRegistry.register('LandingPage', reducer);
